import axios from "axios";
import logger from "@/lib/logger";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  withCredentials: true, // send session_token cookie cross-origin
});

// Attach session_token from localStorage as Authorization Bearer fallback
// (covers cases where SameSite=None cookies are blocked).
api.interceptors.request.use((config) => {
  try {
    const t = localStorage.getItem("session_token");
    if (t) {
      config.headers = config.headers || {};
      if (!config.headers.Authorization) {
        config.headers.Authorization = `Bearer ${t}`;
      }
    }
  } catch (err) {
    // localStorage access can throw in private mode or when disabled —
    // requests still work via the httpOnly cookie.
    logger.warn("Auth header attach skipped (localStorage unavailable)", err);
  }
  return config;
});

export const AuthAPI = {
  exchangeSession: (sessionId) =>
    api
      .post("/auth/session", null, { headers: { "X-Session-ID": sessionId } })
      .then((r) => r.data),
  me: () => api.get("/auth/me").then((r) => r.data),
  logout: () => api.post("/auth/logout").then((r) => r.data),
  submitOnboarding: (mode) =>
    api.post("/onboarding", { mode }).then((r) => r.data),
};

export const VehiclesAPI = {
  list: () => api.get("/vehicles").then((r) => r.data),
  create: (data) => api.post("/vehicles", data).then((r) => r.data),
  update: (id, data) => api.put(`/vehicles/${id}`, data).then((r) => r.data),
  remove: (id) => api.delete(`/vehicles/${id}`).then((r) => r.data),
  lastTelemetry: (id) => api.get(`/vehicles/${id}/last-telemetry`).then((r) => r.data),
  // Authoritative current odometer. NEVER derive this in a component or
  // hook — always call through here so OEM telemetry becomes the source
  // the moment a provider is wired into `backend/services/odometer.py`.
  odometer: (id) => api.get(`/vehicles/${id}/odometer`).then((r) => r.data),
};

export const TripsAPI = {
  list: (params) => api.get("/trips", { params }).then((r) => r.data),
  create: (data) => api.post("/trips", data).then((r) => r.data),
  update: (id, data) => api.put(`/trips/${id}`, data).then((r) => r.data),
  remove: (id) => api.delete(`/trips/${id}`).then((r) => r.data),
  csvImport: (vehicle_id, unit, file) => {
    const fd = new FormData();
    fd.append("file", file);
    return api
      .post(`/trips/csv-import`, fd, {
        params: { vehicle_id, unit },
      })
      .then((r) => r.data);
  },
  // Ephemeral dashboard photo OCR — image is processed on the server then
  // discarded. Returns { end_odometer_km, end_battery_pct, fuel_level_pct }.
  scanDashboard: (image_base64, mime_type = "image/jpeg") =>
    api.post("/trips/scan-dashboard", { image_base64, mime_type }).then((r) => r.data),
};

export const StatsAPI = {
  get: (params) => api.get("/stats", { params }).then((r) => r.data),
};

export const EnergyAPI = {
  list: (params) => api.get("/energy-events", { params }).then((r) => r.data),
  create: (data) => api.post("/energy-events", data).then((r) => r.data),
  update: (id, data) => api.put(`/energy-events/${id}`, data).then((r) => r.data),
  remove: (id) => api.delete(`/energy-events/${id}`).then((r) => r.data),
  csvImport: (vehicle_id, type, file) => {
    const fd = new FormData();
    fd.append("file", file);
    return api
      .post(`/energy-events/csv-import`, fd, { params: { vehicle_id, type } })
      .then((r) => r.data);
  },
};

export const SettingsAPI = {
  get: () => api.get("/settings").then((r) => r.data),
  update: (data) => api.put("/settings", data).then((r) => r.data),
};

export const ComponentsAPI = {
  list: (params) => api.get("/components", { params }).then((r) => r.data),
  summary: (params) => api.get("/components/summary", { params }).then((r) => r.data),
  create: (data) => api.post("/components", data).then((r) => r.data),
  update: (id, data) => api.put(`/components/${id}`, data).then((r) => r.data),
  remove: (id) => api.delete(`/components/${id}`).then((r) => r.data),
};

export const MaintenanceAPI = {
  list: (params) => api.get("/maintenance", { params }).then((r) => r.data),
  summary: (params) => api.get("/maintenance/summary", { params }).then((r) => r.data),
  create: (data) => api.post("/maintenance", data).then((r) => r.data),
  update: (id, data) => api.put(`/maintenance/${id}`, data).then((r) => r.data),
  remove: (id) => api.delete(`/maintenance/${id}`).then((r) => r.data),
  complete: (id, data) => api.post(`/maintenance/${id}/complete`, data).then((r) => r.data),
  history: (id) => api.get(`/maintenance/${id}/history`).then((r) => r.data),
  reseed: (vehicleId) => api.post(`/maintenance/reseed/${vehicleId}`).then((r) => r.data),
};

export const BACKEND_ORIGIN = API.replace(/\/api$/, "");

/**
 * Resolve a stored image_url (which may be relative like "/api/uploads/<id>"
 * or absolute) into something `<img src>` can render.
 */
export function resolveImageUrl(value) {
  if (!value) return null;
  if (value.startsWith("http://") || value.startsWith("https://") || value.startsWith("data:")) return value;
  if (value.startsWith("/api/")) return `${BACKEND_ORIGIN}${value}`;
  return value;
}

export const FeedbackAPI = {
  send: (payload) => api.post("/feedback", payload).then((r) => r.data),
};

export const UploadsAPI = {
  // Upload a vehicle photo blob; returns { file_id, url }. The url is a
  // backend-relative path that the API base already prefixes — for <img src>
  // we use `${API}${url}`.
  uploadVehiclePhoto: async (blob, filename = "photo.jpg") => {
    const form = new FormData();
    form.append("file", blob, filename);
    // Let axios set the multipart boundary; do NOT force Content-Type here.
    const res = await api.post("/uploads/vehicle-photo", form);
    return res.data;
  },
};

export const AccountAPI = {
  // Triggers a browser download of the user's ZIP'd CSV export. We bypass the
  // axios instance for blob handling and use fetch with credentials so the
  // file lands on disk via a temporary object URL.
  // `includeArchived=true` grabs trips/energy for archived vehicles too;
  // default matches dashboard visibility.
  exportData: async ({ includeArchived = false } = {}) => {
    const token = (() => {
      try { return localStorage.getItem("session_token") || null; } catch { return null; }
    })();
    const qs = includeArchived ? "?include_archived=true" : "";
    const res = await fetch(`${API}/account/export${qs}`, {
      method: "GET",
      credentials: "include",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) throw new Error(`Export failed (${res.status})`);
    const blob = await res.blob();
    // Pull filename from Content-Disposition when present.
    const cd = res.headers.get("content-disposition") || "";
    const match = /filename="?([^";]+)"?/i.exec(cd);
    const filename = match ? match[1] : `mileageiq-export-${new Date().toISOString().replace(/[:.]/g, "-")}.zip`;
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    return { filename };
  },
  deleteAccount: () => api.delete("/account").then((r) => r.data),
};

export const seedAPI = () => api.post("/seed").then((r) => r.data);
